import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, ShieldCheck, MapPin, Video, FolderLock, CalendarCheck, Share2, Eye, EyeOff } from "lucide-react";

const PILLARS = [
  [FolderLock, "One repository", "Title deeds, ECs, pahani, survey maps and legal opinions filed per property."],
  [CalendarCheck, "Monitored on schedule", "Field executives capture geotagged, timestamped evidence every cycle."],
  [Video, "Live CCTV", "Watch a site on demand through your camera or NVR feed."],
];

const PROOF = [
  [MapPin, "Geotagged site visits"],
  [ShieldCheck, "Full audit trail"],
  [Share2, "Bank-ready share links"],
];

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.05fr_1fr]">
      {/* ---------- brand panel ---------- */}
      <div className="relative hidden lg:flex flex-col justify-between bg-zinc-950 px-14 py-12 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.10]"
          style={{
            backgroundImage:
              "linear-gradient(to right, #6366F1 1px, transparent 1px), linear-gradient(to bottom, #6366F1 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
        <div className="absolute -top-40 -left-28 h-[26rem] w-[26rem] rounded-full bg-primary/25 blur-3xl" />
        <div className="absolute -bottom-24 right-0 h-80 w-80 rounded-full bg-cyan-500/15 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.18] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='140' height='140' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")",
          }}
        />

        <div className="relative rise">
          <Logo size={34} tone="light" />
        </div>

        <div className="relative max-w-2xl">
          <span className="rise inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">
              All-in-one asset monitoring
            </span>
          </span>

          <h1 className="rise mt-7 font-display text-4xl xl:text-[3.4rem] font-bold tracking-tighter text-white leading-[1.02]"
            style={{ animationDelay: "60ms" }}>
            A centralised repository
            <br />
            <span className="text-primary">with eyes on every site.</span>
          </h1>

          <p className="rise mt-6 text-lg leading-snug text-zinc-200 max-w-xl" style={{ animationDelay: "120ms" }}>
            Every document in one place, and every property checked on schedule by your field
            executives — not once a year, but every cycle.
          </p>

          <p className="rise mt-4 text-sm leading-relaxed text-zinc-400 max-w-xl" style={{ animationDelay: "160ms" }}>
            Title records, encumbrance certificates, mortgages, court matters, vaastu notes and
            geotagged field evidence across your agricultural, commercial and residential holdings.
          </p>

          <div className="mt-11 grid grid-cols-3 gap-3">
            {PILLARS.map(([Icon, label, desc], i) => (
              <div key={label} style={{ animationDelay: `${220 + i * 70}ms` }}
                className="rise group rounded-xl border border-zinc-800 bg-zinc-900/50 p-4 transition-colors duration-300 hover:border-primary/50 hover:bg-zinc-900">
                <Icon size={18} className="text-primary transition-transform duration-300 group-hover:-translate-y-0.5" />
                <p className="mt-3.5 text-[13px] font-semibold text-white leading-snug">{label}</p>
                <p className="mt-1.5 text-[11px] text-zinc-500 leading-snug">{desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative flex flex-wrap items-center gap-x-7 gap-y-2 rise" style={{ animationDelay: "440ms" }}>
          {PROOF.map(([Icon, label]) => (
            <span key={label} className="flex items-center gap-2 text-[11px] font-medium text-zinc-500">
              <Icon size={13} className="text-zinc-600" /> {label}
            </span>
          ))}
        </div>
      </div>

      {/* ---------- sign in ---------- */}
      <div className="flex items-center justify-center px-6 py-14 sm:px-10">
        <form onSubmit={submit} className="w-full max-w-sm rise" data-testid="login-form">
          <div className="lg:hidden mb-9">
            <Logo size={30} tone="dark" />
            <h1 className="mt-7 font-display text-[1.7rem] font-bold tracking-tighter leading-[1.1]">
              A centralised repository with eyes on every site.
            </h1>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              Every document in one place, and every property checked on schedule by your field
              executives.
            </p>
          </div>

          <p className="label-tiny">Sign in</p>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tighter">Welcome back</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Use the credentials issued by your administrator.
          </p>

          <div className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email" className="label-tiny">Work email</Label>
              <Input id="email" type="email" data-testid="login-email-input" value={email}
                onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
                className="mt-2 h-11" placeholder="you@company.in" />
            </div>
            <div>
              <Label htmlFor="password" className="label-tiny">Password</Label>
              <div className="relative mt-2">
                <Input id="password" type={showPassword ? "text" : "password"}
                  data-testid="login-password-input" value={password}
                  onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password"
                  className="h-11 pr-11" placeholder="••••••••" />
                <button type="button" data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-1 top-1/2 -translate-y-1/2 grid h-9 w-9 place-items-center rounded-md text-muted-foreground transition-colors duration-200 hover:text-foreground">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          </div>

          {error && (
            <p data-testid="login-error" className="mt-4 text-sm text-destructive">{error}</p>
          )}

          <Button type="submit" disabled={busy} data-testid="login-submit-button"
            className="mt-8 w-full h-11 rounded-lg group active:scale-[0.98] transition-transform duration-150">
            {busy ? "Signing in…" : "Sign in"}
            <ArrowRight size={16} className="ml-2 transition-transform duration-200 group-hover:translate-x-1" />
          </Button>

          <p className="mt-10 text-[11px] text-muted-foreground">
            Field executives: open this page on your phone to record a site visit.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-[11px]">
            <Link to="/privacy" data-testid="login-privacy-link"
              className="font-semibold text-muted-foreground hover:text-primary transition-colors duration-200">
              Privacy Policy
            </Link>
            <Link to="/terms" data-testid="login-terms-link"
              className="font-semibold text-muted-foreground hover:text-primary transition-colors duration-200">
              Terms of Service
            </Link>
            <span className="text-muted-foreground/70">© {new Date().getFullYear()} REAM</span>
          </div>
        </form>
      </div>
    </div>
  );
}
